import { useState } from 'react';
import {
  Code,
  Book,
  Zap,
  Terminal,
  Copy,
  CheckCircle,
  ExternalLink,
  Boxes,
  Workflow,
  Shield,
  Clock,
  ArrowRight,
  PlayCircle,
  FileCode,
  Cpu
} from 'lucide-react';
import styles from './Developers.module.css';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

export default function Developers() {
  const [copiedId, setCopiedId] = useState(null);
  const [activeTab, setActiveTab] = useState('quickstart');

  function copyToClipboard(text, id) {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  }

  const CodeBlock = ({ code, language = 'bash', id }) => (
    <div className={styles.codeBlock}>
      <div className={styles.codeBlockHeader}>
        <span className={styles.codeBlockLang}>{language}</span>
        <button onClick={() => copyToClipboard(code, id)} className={styles.codeBlockCopyBtn}>
          {copiedId === id ? (
            <><CheckCircle size={14} className={styles.iconSuccess} />Copied!</>
          ) : (
            <><Copy size={14} />Copy</>
          )}
        </button>
      </div>
      <pre className={styles.codeBlockPre}>
        <code className={styles.codeBlockCode}>{code}</code>
      </pre>
    </div>
  );

  return (
    <div className={styles.page}>
      {/* Hero */}
      <div className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroBadge}>
            <Cpu size={16} />
            Infrastructure for Time
          </div>
          <h1 className={styles.heroTitle}>Build with Vennio</h1>
          <p className={styles.heroSubtitle}>
            Schedule on behalf of anyone — with their consent. REST API and MCP server for developers and AI agents.
          </p>
          <div className={styles.heroCtas}>
            <a href="#quickstart" className={styles.heroPrimaryBtn}>
              <PlayCircle size={20} />
              Quick Start
            </a>
            <a
              href={`${API_URL}/.well-known/ai-agent`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.heroSecondaryBtn}
            >
              <FileCode size={20} />
              API Discovery
              <ExternalLink size={16} />
            </a>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className={styles.statsBar}>
        <div className={styles.statsBarInner}>
          <div className={styles.statsGrid}>
            <div className={styles.statItem}>
              <div className={`${styles.statValue} ${styles.statValueAccent}`}>3</div>
              <div className={styles.statLabel}>API calls to book</div>
            </div>
            <div className={styles.statItem}>
              <div className={`${styles.statValue} ${styles.statValueAccent}`}>&lt;500ms</div>
              <div className={styles.statLabel}>Typical response time</div>
            </div>
            <div className={styles.statItem}>
              <div className={`${styles.statValue} ${styles.statValueAccent}`}>100%</div>
              <div className={styles.statLabel}>OpenAPI 3.0 coverage</div>
            </div>
            <div className={styles.statItem}>
              <div className={`${styles.statValue} ${styles.statValueSuccess}`}>99.9%</div>
              <div className={styles.statLabel}>Uptime SLA</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main */}
      <div className={styles.main}>
        {/* Tab nav */}
        <nav className={styles.tabNav}>
          {[
            { id: 'quickstart',     icon: <Zap size={18} />,      label: 'Quick Start' },
            { id: 'workflows',      icon: <Workflow size={18} />,  label: 'Workflows' },
            { id: 'authentication', icon: <Shield size={18} />,    label: 'Authentication' },
            { id: 'examples',       icon: <Code size={18} />,      label: 'Code Examples' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`${styles.tabBtn}${activeTab === tab.id ? ` ${styles.tabBtnActive}` : ''}`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Quick Start Tab */}
        {activeTab === 'quickstart' && (
          <div className={styles.tabContent}>
            <div className={styles.card}>
              <h2 className={styles.cardTitle} id="quickstart">Get Started in 3 Steps</h2>

              <div className={styles.step}>
                <div className={styles.stepNum}>1</div>
                <div className={styles.stepBody}>
                  <h3 className={styles.stepTitle}>Get your API key</h3>
                  <p className={styles.stepDesc}>Create an API key from your dashboard. It takes 10 seconds.</p>
                  <a href="/api-keys" className={styles.accentBtn}>
                    Create API Key
                    <ArrowRight size={16} />
                  </a>
                </div>
              </div>

              <div className={styles.step}>
                <div className={styles.stepNum}>2</div>
                <div className={styles.stepBody}>
                  <h3 className={styles.stepTitle}>Make your first request</h3>
                  <p className={styles.stepDesc}>List available booking links to see what's possible.</p>
                  <CodeBlock
                    id="first-request"
                    code={`curl ${API_URL}/v1/venn-links \\\n  -H "Authorization: Bearer YOUR_API_KEY"`}
                  />
                </div>
              </div>

              <div className={styles.step}>
                <div className={styles.stepNum}>3</div>
                <div className={styles.stepBody}>
                  <h3 className={styles.stepTitle}>Book a meeting</h3>
                  <p className={styles.stepDesc}>Check availability and create a booking programmatically.</p>
                  <CodeBlock
                    id="book-meeting"
                    language="bash"
                    code={`# 1. Check availability
curl ${API_URL}/v1/principals/PRINCIPAL_ID/availability?duration=30 \\
  -H "Authorization: Bearer YOUR_API_KEY"

# 2. Create booking
curl -X POST ${API_URL}/v1/venn-links/VENN_LINK_ID/bookings \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "customer_name": "Alex Chen",
    "customer_email": "alex@example.com",
    "start_time": "2026-01-25T14:00:00Z",
    "notes": "Excited to chat!"
  }'`}
                  />
                </div>
              </div>
            </div>

            {/* What You Can Build */}
            <div className={styles.buildSection}>
              <h2 className={styles.cardTitle}>What You Can Build</h2>
              <div className={styles.buildGrid}>
                <div className={styles.buildCard}>
                  <div className={styles.buildCardHeader}>
                    <div className={styles.iconWrapAccent}><Cpu className={styles.iconAccent} size={20} /></div>
                    <h3 className={styles.buildCardTitle}>AI Agents</h3>
                  </div>
                  <p className={styles.buildCardDesc}>Build autonomous agents that schedule meetings without human intervention. Perfect for Claude, ChatGPT, and custom AI assistants.</p>
                </div>

                <div className={styles.buildCard}>
                  <div className={styles.buildCardHeader}>
                    <div className={styles.iconWrapAccent}><Workflow className={styles.iconAccent} size={20} /></div>
                    <h3 className={styles.buildCardTitle}>Workflow Automation</h3>
                  </div>
                  <p className={styles.buildCardDesc}>Integrate with Zapier, Make, or n8n to automate scheduling across your entire tech stack.</p>
                </div>

                <div className={styles.buildCard}>
                  <div className={styles.buildCardHeader}>
                    <div className={styles.iconWrapAccent}><Boxes className={styles.iconAccent} size={20} /></div>
                    <h3 className={styles.buildCardTitle}>Custom Applications</h3>
                  </div>
                  <p className={styles.buildCardDesc}>Build scheduling into your product. Add calendar coordination to your SaaS, marketplace, or platform.</p>
                </div>

                <div className={styles.buildCard}>
                  <div className={styles.buildCardHeader}>
                    <div className={styles.iconWrapSuccess}><Terminal className={styles.iconSuccess} size={20} /></div>
                    <h3 className={styles.buildCardTitle}>CRM Integration</h3>
                  </div>
                  <p className={styles.buildCardDesc}>Sync bookings with Salesforce, HubSpot, or any CRM. Keep your sales team's calendar in sync automatically.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Workflows Tab */}
        {activeTab === 'workflows' && (
          <div className={styles.tabContent}>
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>Common Workflows</h2>

              <div className={styles.workflowSection}>
                <h3 className={styles.workflowTitle}>
                  <div className={styles.workflowTitleIcon}><Clock className={styles.iconAccent} size={16} /></div>
                  Book a Meeting
                </h3>
                <p className={styles.workflowDesc}>The complete flow from discovering availability to creating a confirmed booking.</p>

                <div className={styles.workflowSteps}>
                  <div className={styles.step}>
                    <div className={styles.stepNumSm}>1</div>
                    <div className={styles.stepBody}>
                      <h4 className={styles.stepTitle}>List booking links</h4>
                      <CodeBlock id="workflow-1-step-1" code={`GET ${API_URL}/v1/venn-links`} />
                      <p className={styles.stepNote}>Returns: Array of available booking links with titles, durations, and IDs</p>
                    </div>
                  </div>

                  <div className={styles.step}>
                    <div className={styles.stepNumSm}>2</div>
                    <div className={styles.stepBody}>
                      <h4 className={styles.stepTitle}>Check availability</h4>
                      <CodeBlock id="workflow-1-step-2" code={`GET ${API_URL}/v1/principals/PRINCIPAL_ID/availability?duration=30&after=2026-01-24T00:00:00Z`} />
                      <p className={styles.stepNote}>Returns: Array of available time slots with start/end times</p>
                    </div>
                  </div>

                  <div className={styles.step}>
                    <div className={styles.stepNumSm}>3</div>
                    <div className={styles.stepBody}>
                      <h4 className={styles.stepTitle}>Create booking</h4>
                      <CodeBlock
                        id="workflow-1-step-3"
                        language="bash"
                        code={`curl -X POST ${API_URL}/v1/venn-links/VENN_LINK_ID/bookings \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "customer_name": "Alex Chen",
    "customer_email": "alex@example.com",
    "start_time": "2026-01-25T14:00:00Z",
    "notes": "Looking forward to our call"
  }'`}
                      />
                      <div className={styles.infoBoxSuccess}>
                        <div className={styles.infoBoxSuccessContent}>
                          <CheckCircle size={18} className={styles.iconSuccess} style={{ flexShrink: 0, marginTop: 2 }} />
                          <div>
                            <div className={styles.infoBoxSuccessTitle}>Success!</div>
                            <div className={styles.infoBoxSuccessText}>Meeting booked, confirmation emails sent, webhooks fired</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.workflowSection}>
                <h3 className={styles.workflowTitle}>
                  <div className={styles.workflowTitleIcon}><Zap className={styles.iconAccent} size={16} /></div>
                  Setup Webhook Notifications
                </h3>
                <p className={styles.workflowDesc}>Receive real-time notifications when bookings are created or cancelled.</p>

                <CodeBlock
                  id="webhook-setup"
                  language="bash"
                  code={`curl -X POST ${API_URL}/v1/webhooks \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://your-app.com/webhooks/vennio",
    "events": ["booking.created", "booking.cancelled"]
  }'`}
                />

                <div className={styles.infoBoxAccent}>
                  <h4 className={styles.infoBoxAccentTitle}>Webhook Payload Example</h4>
                  <CodeBlock
                    id="webhook-payload"
                    language="json"
                    code={`{
  "event": "booking.created",
  "data": {
    "booking_id": "bkg_123",
    "customer_name": "Alex Chen",
    "customer_email": "alex@example.com",
    "start_time": "2026-01-25T14:00:00Z",
    "end_time": "2026-01-25T14:30:00Z",
    "venn_link_id": "vl_abc123"
  },
  "timestamp": "2026-01-25T12:00:00Z"
}`}
                  />
                  <p className={styles.infoBoxAccentNote}>
                    <strong>Security:</strong> Verify webhook signatures using HMAC-SHA256 in the X-Vennio-Signature header
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Authentication Tab */}
        {activeTab === 'authentication' && (
          <div className={styles.tabContent}>
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>Authentication</h2>
              <p className={styles.authDesc}>
                All API requests require authentication using an API key. Include your key in the Authorization header:
              </p>

              <CodeBlock
                id="auth-header"
                code={`Authorization: Bearer vennio_sk_live_YOUR_KEY_HERE`}
              />

              <div className={styles.authGrid}>
                <div className={styles.authCard}>
                  <h3 className={styles.authCardTitle}>
                    <Shield size={18} />
                    Security Best Practices
                  </h3>
                  <ul className={styles.authCardList}>
                    <li>Store keys in environment variables</li>
                    <li>Never commit keys to version control</li>
                    <li>Use different keys for dev/staging/prod</li>
                    <li>Rotate keys regularly</li>
                    <li>Revoke unused keys immediately</li>
                  </ul>
                </div>

                <div className={styles.authCard}>
                  <h3 className={styles.authCardTitle}>
                    <Clock size={18} />
                    Rate Limiting
                  </h3>
                  <ul className={styles.authCardList}>
                    <li>Default: 100 requests/hour</li>
                    <li>Premium: 1000 requests/hour</li>
                    <li>Headers: X-RateLimit-* in responses</li>
                    <li>429 status when limit exceeded</li>
                    <li>Limits reset every hour</li>
                  </ul>
                </div>
              </div>

              <div className={styles.authCta}>
                <h3 className={styles.authCtaTitle}>Create Your First API Key</h3>
                <a href="/api-keys" className={`${styles.accentBtn} ${styles.accentBtnLg}`}>
                  Go to API Keys
                  <ArrowRight size={18} />
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Code Examples Tab */}
        {activeTab === 'examples' && (
          <div className={styles.tabContent}>
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>Code Examples</h2>

              <div className={styles.exampleSection}>
                <h3 className={styles.exampleTitle}>JavaScript / Node.js</h3>
                <CodeBlock
                  id="js-example"
                  language="javascript"
                  code={`const VENNIO_API_KEY = process.env.VENNIO_API_KEY;
const BASE_URL = '${API_URL}';

async function bookMeeting() {
  // 1. Get booking links
  const linksResponse = await fetch(\`\${BASE_URL}/v1/venn-links\`, {
    headers: {
      'Authorization': \`Bearer \${VENNIO_API_KEY}\`
    }
  });
  const { venn_links } = await linksResponse.json();
  const vennLink = venn_links[0];

  // 2. Check availability
  const availResponse = await fetch(
    \`\${BASE_URL}/v1/principals/\${vennLink.principal_id}/availability?duration=30\`,
    {
      headers: {
        'Authorization': \`Bearer \${VENNIO_API_KEY}\`
      }
    }
  );
  const { availability } = await availResponse.json();
  const slot = availability[0];

  // 3. Create booking
  const booking = await fetch(\`\${BASE_URL}/v1/venn-links/\${vennLink.id}/bookings\`, {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${VENNIO_API_KEY}\`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      customer_name: 'Alex Chen',
      customer_email: 'alex@example.com',
      start_time: slot.start,
      notes: 'Excited for our call!'
    })
  });

  return await booking.json();
}

bookMeeting().then(console.log);`}
                />
              </div>

              <div className={styles.exampleSection}>
                <h3 className={styles.exampleTitle}>Python</h3>
                <CodeBlock
                  id="python-example"
                  language="python"
                  code={`import os
import requests

VENNIO_API_KEY = os.environ['VENNIO_API_KEY']
BASE_URL = '${API_URL}'

def book_meeting():
    headers = {'Authorization': f'Bearer {VENNIO_API_KEY}'}

    # 1. Get booking links
    links_response = requests.get(f'{BASE_URL}/v1/venn-links', headers=headers)
    venn_links = links_response.json()['venn_links']
    venn_link = venn_links[0]

    # 2. Check availability
    avail_response = requests.get(
        f"{BASE_URL}/v1/principals/{venn_link['principal_id']}/availability",
        params={'duration': 30},
        headers=headers
    )
    availability = avail_response.json()['availability']
    slot = availability[0]

    # 3. Create booking
    booking_response = requests.post(
        f"{BASE_URL}/v1/venn-links/{venn_link['id']}/bookings",
        json={
            'customer_name': 'Alex Chen',
            'customer_email': 'alex@example.com',
            'start_time': slot['start'],
            'notes': 'Excited for our call!'
        },
        headers={**headers, 'Content-Type': 'application/json'}
    )

    return booking_response.json()

print(book_meeting())`}
                />
              </div>

              <div className={styles.exampleSection}>
                <h3 className={styles.exampleTitle}>cURL</h3>
                <CodeBlock
                  id="curl-example"
                  language="bash"
                  code={`# Set your API key
export VENNIO_API_KEY="vennio_sk_live_YOUR_KEY"

# 1. List booking links
curl ${API_URL}/v1/venn-links \\
  -H "Authorization: Bearer $VENNIO_API_KEY"

# 2. Check availability (replace PRINCIPAL_ID)
curl "${API_URL}/v1/principals/PRINCIPAL_ID/availability?duration=30" \\
  -H "Authorization: Bearer $VENNIO_API_KEY"

# 3. Create booking (replace VENN_LINK_ID)
curl -X POST ${API_URL}/v1/venn-links/VENN_LINK_ID/bookings \\
  -H "Authorization: Bearer $VENNIO_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "customer_name": "Alex Chen",
    "customer_email": "alex@example.com",
    "start_time": "2026-01-25T14:00:00Z",
    "notes": "Looking forward to the call"
  }'`}
                />
              </div>
            </div>
          </div>
        )}

        {/* Resources */}
        <div className={styles.resourcesGrid}>
          <a
            href="https://github.com/vennio-hq/my-porter/blob/main/docs/DEVELOPER_QUICK_START.md"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.resourceCard}
          >
            <div className={styles.resourceCardHeader}>
              <div className={styles.iconWrapSuccess}><Book className={styles.iconSuccess} size={20} /></div>
              <h3 className={styles.resourceName}>Documentation</h3>
            </div>
            <p className={styles.resourceDesc}>Complete developer guide with examples</p>
            <div className={`${styles.resourceLink} ${styles.resourceLinkSuccess}`}>
              Read Docs <ExternalLink size={14} />
            </div>
          </a>

          <a
            href={`${API_URL}/.well-known/ai-agent`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.resourceCard}
          >
            <div className={styles.resourceCardHeader}>
              <div className={styles.iconWrapAccent}><Cpu className={styles.iconAccent} size={20} /></div>
              <h3 className={styles.resourceName}>AI Discovery</h3>
            </div>
            <p className={styles.resourceDesc}>Machine-readable API capabilities for AI agents</p>
            <div className={`${styles.resourceLink} ${styles.resourceLinkAccent}`}>
              View Discovery <ExternalLink size={14} />
            </div>
          </a>

          <a
            href={`${API_URL}/openapi.yaml`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.resourceCard}
          >
            <div className={styles.resourceCardHeader}>
              <div className={styles.iconWrapAccent}><FileCode className={styles.iconAccent} size={20} /></div>
              <h3 className={styles.resourceName}>OpenAPI Spec</h3>
            </div>
            <p className={styles.resourceDesc}>Full OpenAPI 3.0 specification with examples</p>
            <div className={`${styles.resourceLink} ${styles.resourceLinkAccent}`}>
              Download Spec <ExternalLink size={14} />
            </div>
          </a>

          <a href="/api-keys" className={styles.resourceCard}>
            <div className={styles.resourceCardHeader}>
              <div className={styles.iconWrapAccent}><Shield className={styles.iconAccent} size={20} /></div>
              <h3 className={styles.resourceName}>API Keys</h3>
            </div>
            <p className={styles.resourceDesc}>Create and manage your API keys</p>
            <div className={`${styles.resourceLink} ${styles.resourceLinkAccent}`}>
              Manage Keys <ArrowRight size={14} />
            </div>
          </a>
        </div>
      </div>
    </div>
  );
}
